import type {
  AuditEvent,
  CostChange,
  FixedCost,
  DismissedHints,
  HintId,
  OnboardingPlan,
  ItemForUser,
  ItemMeta,
  OffboardingPlan,
  AworkDoc,
  LoginMethod,
  Permissions,
  PublicUser,
  SessionInfo,
  VaultKeyBundle,
  EncryptedBlob,
} from '@shared/types';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(path, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });

  if (!res.ok) {
    let message = `${res.status} ${res.statusText}`;
    try {
      const data = (await res.json()) as { error?: string };
      if (data.error) message = data.error;
    } catch {
      // Antwort war kein JSON - dann bleibt der Statustext stehen.
    }
    throw new ApiError(res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type FixedCostInput = Omit<FixedCost, 'id' | 'createdAt' | 'updatedAt' | 'updatedBy'>;

export type ItemInput = Omit<
  ItemMeta,
  'id' | 'createdAt' | 'updatedAt' | 'updatedBy' | 'needsRotation' | 'rotationReason' | 'lastRotatedAt'
>;

/** Was der Server braucht, um einen Platz anzulegen oder zu ändern. */
export interface AssignmentInput {
  label: string;
  assigneeUids: string[];
  maxUsers: number | null;
  openToAll: boolean;
  device: string;
  loginMethod: LoginMethod;
  /** null, wenn zu diesem Platz kein eigenes Geheimnis hinterlegt ist. */
  secret: EncryptedBlob | null;
  access: Record<string, string>;
}

export const api = {
  session: () => request<SessionInfo>('GET', '/api/session'),
  createVaultKey: (bundle: VaultKeyBundle) =>
    request<{ ok: true }>('POST', '/api/session/vault-key', bundle),
  rewrapVaultKey: (bundle: VaultKeyBundle) =>
    request<{ ok: true }>('POST', '/api/session/vault-key/rewrap', bundle),
  resetVaultKey: () =>
    request<{ ok: true; revoked: number }>('POST', '/api/session/vault-key/reset'),
  logout: () => request<{ ok: true }>('POST', '/auth/logout'),

  /** `until: null` ignoriert dauerhaft, ein Datum stellt zurück, `undefined` holt zurück. */
  dismissHint: (hintId: HintId, until?: string | null) =>
    request<{ dismissedHints: DismissedHints }>('PATCH', '/api/session/hints', {
      hintId,
      ...(until === undefined ? {} : { until }),
    }).then((r) => r.dismissedHints),

  fetchIcon: (url: string) =>
    request<{ dataUri: string; host: string }>('POST', '/api/icons/fetch', { url }),

  aworkDocuments: (q: string) =>
    request<{ available: boolean; documents: AworkDoc[] }>(
      'GET',
      `/api/awork/documents?q=${encodeURIComponent(q)}`,
    ),

  users: () => request<{ users: PublicUser[] }>('GET', '/api/users').then((r) => r.users),
  updateUser: (
    uid: string,
    patch: {
      role?: 'admin' | 'member';
      status?: 'active' | 'disabled';
      permissions?: Partial<Permissions>;
    },
  ) =>
    request<{ ok: true }>('PATCH', `/api/users/${uid}`, patch),
  deleteUser: (uid: string) => request<{ ok: true }>('DELETE', `/api/users/${uid}`),
  setOwnPicture: (dataUrl: string | null) =>
    request<{ user: PublicUser }>('PUT', '/api/profile/picture', { dataUrl }),

  welcome: () => request<{ text: string; isDefault: boolean }>('GET', '/api/welcome'),
  setWelcome: (text: string) =>
    request<{ text: string; isDefault: boolean }>('PUT', '/api/welcome', { text }),

  items: () => request<{ items: ItemForUser[] }>('GET', '/api/items').then((r) => r.items),
  createItem: (payload: {
    meta: ItemInput;
    secret: EncryptedBlob;
    access: Record<string, string>;
    assignments?: AssignmentInput[];
  }) => request<{ item: ItemForUser }>('POST', '/api/items', payload).then((r) => r.item),
  updateItem: (
    id: string,
    payload: {
      meta: ItemInput;
      secret: EncryptedBlob;
      access: Record<string, string>;
      rotated: boolean;
    },
  ) => request<{ item: ItemForUser }>('PUT', `/api/items/${id}`, payload).then((r) => r.item),
  patchMeta: (id: string, patch: Partial<ItemInput>) =>
    request<{ ok: true }>('PATCH', `/api/items/${id}/meta`, patch),
  deleteItem: (id: string) => request<{ ok: true }>('DELETE', `/api/items/${id}`),
  addGrants: (id: string, grants: Record<string, string>) =>
    request<{ ok: true; accessUids: string[] }>('POST', `/api/items/${id}/grants`, { grants }),
  revokeGrant: (id: string, uid: string) =>
    request<{ ok: true; accessUids: string[] }>('DELETE', `/api/items/${id}/grants/${uid}`),
  regrant: (targetUid: string, grants: { itemId: string; wrappedKey: string }[]) =>
    request<{ ok: true; applied: number }>('POST', '/api/items/regrant', { targetUid, grants }),

  addAssignment: (itemId: string, input: AssignmentInput) =>
    request<{ item: ItemForUser }>('POST', `/api/items/${itemId}/assignments`, input).then(
      (r) => r.item,
    ),
  updateAssignment: (itemId: string, assignmentId: string, input: AssignmentInput) =>
    request<{ item: ItemForUser }>(
      'PATCH',
      `/api/items/${itemId}/assignments/${assignmentId}`,
      input,
    ).then((r) => r.item),
  deleteAssignment: (itemId: string, assignmentId: string) =>
    request<{ item: ItemForUser }>('DELETE', `/api/items/${itemId}/assignments/${assignmentId}`).then(
      (r) => r.item,
    ),

  onboardingPlans: () =>
    request<{ plans: OnboardingPlan[] }>('GET', '/api/onboarding').then((r) => r.plans),
  startOnboarding: (uid: string) =>
    request<{ plan: OnboardingPlan }>('POST', `/api/onboarding/${uid}/start`).then((r) => r.plan),
  checkOnboardingEntry: (uid: string, itemId: string, done: boolean) =>
    request<{ plan: OnboardingPlan }>('PATCH', `/api/onboarding/${uid}/entries`, {
      itemId,
      done,
    }).then((r) => r.plan),
  completeOnboarding: (uid: string) =>
    request<{ plan: OnboardingPlan }>('POST', `/api/onboarding/${uid}/complete`).then((r) => r.plan),

  offboardingPlans: () =>
    request<{ plans: OffboardingPlan[] }>('GET', '/api/offboarding').then((r) => r.plans),
  startOffboarding: (uid: string) =>
    request<{ plan: OffboardingPlan }>('POST', `/api/offboarding/${uid}/start`).then((r) => r.plan),
  checkOffboardingEntry: (
    uid: string,
    itemId: string,
    assignmentId: string | null,
    done: boolean,
  ) =>
    request<{ plan: OffboardingPlan }>('PATCH', `/api/offboarding/${uid}/entries`, {
      itemId,
      assignmentId,
      done,
    }).then((r) => r.plan),
  completeOffboarding: (uid: string) =>
    request<{ plan: OffboardingPlan }>('POST', `/api/offboarding/${uid}/complete`).then(
      (r) => r.plan,
    ),

  audit: () => request<{ events: AuditEvent[] }>('GET', '/api/audit').then((r) => r.events),

  fixedCosts: () =>
    request<{ costs: FixedCost[] }>('GET', '/api/fixed-costs').then((r) => r.costs),
  createFixedCost: (input: FixedCostInput) =>
    request<{ cost: FixedCost }>('POST', '/api/fixed-costs', input).then((r) => r.cost),
  updateFixedCost: (id: string, input: FixedCostInput) =>
    request<{ cost: FixedCost }>('PUT', `/api/fixed-costs/${id}`, input).then((r) => r.cost),
  deleteFixedCost: (id: string) => request<{ ok: true }>('DELETE', `/api/fixed-costs/${id}`),

  clearCostHistory: () =>
    request<{ ok: true; deleted: number }>('DELETE', '/api/cost-history'),

  costHistory: (year?: number) =>
    request<{ year: number; changes: CostChange[]; years: number[] }>(
      'GET',
      year ? `/api/cost-history?year=${year}` : '/api/cost-history',
    ),
};
